import React from 'react';
import {
  C,
  CARD_W,
  SUB_BOMB_SPEED_H,
  nx,
  ny,
} from '../flowCanvas.constants';

const HEADER_H = 44;
const DIAL_SIZE = 96;
const MIN_SPEED = 1;
const MAX_SPEED = 200;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function speedToAngle(speed) {
  const ratio = (clamp(speed, MIN_SPEED, MAX_SPEED) - MIN_SPEED) / (MAX_SPEED - MIN_SPEED);
  return (-135 + ratio * 270) * (Math.PI / 180);
}

function angleToSpeed(angleDeg) {
  const clamped = clamp(angleDeg, -135, 135);
  const ratio = (clamped + 135) / 270;
  return Math.round(MIN_SPEED + ratio * (MAX_SPEED - MIN_SPEED));
}

export default function SubBombSpeedNode({
  step,
  selected,
  onClick,
  centreY,
  overrideX,
  overrideY,
  liveCustomisationData,
  onFieldChange,
}) {
  const x = overrideX ?? nx(step);
  const y = overrideY ?? ny(step, centreY);
  const left = x - CARD_W / 2;
  const top = y - SUB_BOMB_SPEED_H / 2;

  const cfg = step?.customisationConfig;
  const tabKey = cfg?.tabKey;
  const varKey = cfg?.varKey;
  const varName = cfg?.variables?.[0]?.variableName;
  const timing = liveCustomisationData?.[tabKey]?.[varKey]?.[varName]?.[0]
    ?? step?.populatedData?.variables?.find((v) => v.variableName === varName)?.allValues?.[0]
    ?? {};

  const countSpeed = clamp(Number(timing?.countSpeed ?? 100), MIN_SPEED, MAX_SPEED);
  const dialCX = left + CARD_W / 2;
  const dialCY = top + HEADER_H + DIAL_SIZE / 2 + 6;
  const dialR = DIAL_SIZE / 2;
  const handAngle = speedToAngle(countSpeed);

  const setSpeedFromPointer = (event) => {
    event.stopPropagation();
    const svg = event.currentTarget.ownerSVGElement;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const localX = event.clientX - rect.left;
    const localY = event.clientY - rect.top;
    const angleDeg = Math.atan2(localY - dialCY, localX - dialCX) * (180 / Math.PI);
    onFieldChange?.('countSpeed', angleToSpeed(angleDeg));
  };

  return (
    <g className="fc-node fc-node--subbomb-speed" onClick={onClick} style={{ cursor: 'pointer' }}>
      <rect x={left + 2} y={top + 4} width={CARD_W} height={SUB_BOMB_SPEED_H} rx="10" fill="#000" fillOpacity="0.3" />
      <rect
        x={left}
        y={top}
        width={CARD_W}
        height={SUB_BOMB_SPEED_H}
        rx="10"
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : C.purple}
        strokeWidth={selected ? 2 : 1.5}
      />
      <line x1={left + 1} y1={top + HEADER_H} x2={left + CARD_W - 1} y2={top + HEADER_H} stroke={C.border} strokeWidth="1" />

      <text x={left + 14} y={top + 25} fontSize="11.5" fontWeight="700" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {step.stepTitle || 'Count Speed'}
      </text>
      <text x={left + 14} y={top + 39} fontSize="8.5" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        Click the dial to tune the counter speed
      </text>

      <g onMouseDown={setSpeedFromPointer} style={{ cursor: 'crosshair' }}>
        <circle cx={dialCX} cy={dialCY} r={dialR} fill="rgba(145,70,255,0.06)" stroke={C.purple} strokeWidth="1.5" />
        {Array.from({ length: 13 }, (_, index) => {
          const angle = (-135 + index * 22.5) * (Math.PI / 180);
          const inner = dialR - 10;
          const outer = dialR - 2;
          return (
            <line
              key={index}
              x1={dialCX + inner * Math.cos(angle)}
              y1={dialCY + inner * Math.sin(angle)}
              x2={dialCX + outer * Math.cos(angle)}
              y2={dialCY + outer * Math.sin(angle)}
              stroke={index % 3 === 0 ? C.purple : 'rgba(145,70,255,0.30)'}
              strokeWidth={index % 3 === 0 ? 1.5 : 1}
            />
          );
        })}
        <line
          x1={dialCX}
          y1={dialCY}
          x2={dialCX + (dialR - 12) * Math.cos(handAngle)}
          y2={dialCY + (dialR - 12) * Math.sin(handAngle)}
          stroke={C.purple}
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <circle cx={dialCX} cy={dialCY} r="4" fill={C.purple} />
      </g>

      <text x={dialCX} y={dialCY + dialR + 22} textAnchor="middle" fontSize="20" fontWeight="800" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {countSpeed}
      </text>
      <text x={dialCX} y={dialCY + dialR + 37} textAnchor="middle" fontSize="9" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        Count Up Speed
      </text>

      <text x={left + 24} y={top + SUB_BOMB_SPEED_H - 18} fontSize="9" fontWeight="700" fill={C.textDim} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        Slow
      </text>
      <text x={left + CARD_W - 24} y={top + SUB_BOMB_SPEED_H - 18} textAnchor="end" fontSize="9" fontWeight="700" fill={C.textDim} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        Fast
      </text>

      <g onClick={(event) => { event.stopPropagation(); onFieldChange?.('countSpeed', clamp(countSpeed - 5, MIN_SPEED, MAX_SPEED)); }} style={{ cursor: 'pointer' }}>
        <circle cx={left + 36} cy={top + SUB_BOMB_SPEED_H - 38} r="12" fill="rgba(255,255,255,0.06)" stroke="rgba(145,70,255,0.30)" strokeWidth="1" />
        <text x={left + 36} y={top + SUB_BOMB_SPEED_H - 34} textAnchor="middle" fontSize="16" fontWeight="700" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
          -
        </text>
      </g>
      <g onClick={(event) => { event.stopPropagation(); onFieldChange?.('countSpeed', clamp(countSpeed + 5, MIN_SPEED, MAX_SPEED)); }} style={{ cursor: 'pointer' }}>
        <circle cx={left + CARD_W - 36} cy={top + SUB_BOMB_SPEED_H - 38} r="12" fill="rgba(255,255,255,0.06)" stroke="rgba(145,70,255,0.30)" strokeWidth="1" />
        <text x={left + CARD_W - 36} y={top + SUB_BOMB_SPEED_H - 34} textAnchor="middle" fontSize="16" fontWeight="700" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
          +
        </text>
      </g>
    </g>
  );
}